import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { AnimateChangeInHeight } from "components/general/AnimateChangeInHeight";
import { Button, ButtonBorderRadius } from "components/general/Button/Button";
import { Text } from "components/general/Text";
import { NAV_LINKS } from "config/consts";
import { useState } from "react";
import { ReactComponent as PlusIcon } from "../../../img/plus.svg";
import { ReactComponent as MinusIcon } from "../../../img/minus.svg";
import { TABLET_WIDTH, useIsMobile, useIsTablet } from "hooks/useDeviceType";

export const Faq = () => {
  const isMobile = useIsMobile();
  const [cardIdxOpened, setCardIdxOpened] = useState<number | null>(null);

  return (
    <Container>
      <div>
        <Title>Frequently Asked Questions</Title>
        <Text
          textStyle="Title"
          size="Large"
          textCss={css`
            margin-bottom: 48px;
          `}
          shouldScale={true}
        >
          If it isn’t answered here, we’re sure we have the answer on our FAQs page.
        </Text>
        <Button
          buttonType="secondary"
          buttonCss={css`
            padding: ${isMobile ? 16 : 22}px;
            border-radius: ${ButtonBorderRadius.Mid};
            width: fit-content;
          `}
          navigateTo={NAV_LINKS.FAQ}
          target="_blank"
          rel="noreferrer nofollow"
        >
          <Text textStyle="Title" size="Large" shouldScale={true}>
            More FAQs
          </Text>
        </Button>
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 18,
        }}
      >
        {FAQ_CONTENT.map((entry, i) => (
          <CollapsibleCard
            key={entry.question}
            title={entry.question}
            body={entry.answer}
            isOpened={cardIdxOpened === i}
            toggleIsOpened={(isOpened: boolean) => {
              if (isOpened) {
                setCardIdxOpened(i);
              } else {
                setCardIdxOpened(null);
              }
            }}
          />
        ))}
      </div>
    </Container>
  );
};

// Sub-components

const Container = styled.div(() => {
  const tabletMediaQuery = `@media (max-width: ${TABLET_WIDTH}px)`;
  return {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    width: "100%",
    paddingLeft: 135,
    paddingRight: 73,
    gap: 55,
    zIndex: 1,

    [tabletMediaQuery]: {
      gridTemplateRows: "auto auto",
      gridTemplateColumns: "none",
      paddingLeft: "8%",
      paddingRight: "8%",
    },
  };
});

const Title = styled.h3(({ theme }) => {
  const isMobile = useIsMobile();
  return {
    color: theme.font.primary.default,
    fontFamily: "Quincy CF",
    fontSize: isMobile ? 30 : 60,
    fontStyle: "normal",
    fontWeight: 800,
    lineHeight: "111%",
    letterSpacing: -2.4,
    marginBottom: 32,
  };
});

const CollapsibleCard = ({
  title,
  body,
  isOpened,
  toggleIsOpened,
}: {
  title: string;
  body: string;
  isOpened: boolean;
  toggleIsOpened: (isOpened: boolean) => void;
}) => {
  const Symbol = isOpened ? MinusIcon : PlusIcon;
  const isTablet = useIsTablet();
  const isMobile = useIsMobile();
  let iconSize = 40;
  if (isTablet || isMobile) {
    iconSize = 30;
  }
  return (
    <AnimateChangeInHeight>
      <CollapsibleCardContainer isOpened={isOpened} onClick={() => toggleIsOpened(!isOpened)}>
        <div
          style={{ display: "flex", gap: 20, justifyContent: "space-between", userSelect: "none" }}
        >
          <Text textStyle="Title" size="Large" shouldScale={true}>
            {title}
          </Text>
          <PlusOrMinusContainer isOpened={isOpened}>
            <Symbol width={iconSize} height={iconSize} />
          </PlusOrMinusContainer>
        </div>
        {isOpened && (
          <Text
            textStyle="Header"
            size="Medium"
            textCss={css`
              font-family: "Work Sans";
              white-space: pre-line;
            `}
            shouldScale={true}
          >
            {body}
          </Text>
        )}
      </CollapsibleCardContainer>
    </AnimateChangeInHeight>
  );
};

const PlusOrMinusContainer = styled.div<{ isOpened: boolean }>(({ isOpened, theme }) => {
  return {
    display: "flex",
    padding: 8,
    borderRadius: 14,
    backgroundColor: isOpened ? theme.font.primary.default : theme.surface.main.secondary.default,
    aspectRatio: 1,
    maxWidth: 56,
    maxHeight: 56,
  };
});

const CollapsibleCardContainer = styled.div<{ isOpened: boolean }>(({ isOpened, theme }) => {
  return {
    display: "flex",
    flexDirection: "column",
    cursor: "pointer",
    padding: 32,
    gap: 24,
    borderRadius: 24,
    backgroundColor: isOpened ? theme["Brand colours"].Pink : theme.surface.main.primary.default,
    color: isOpened ? theme.button.secondary.text.default : theme.font.primary.default,
    transition: "background-color 0.2s linear",

    "&:hover": {
      backgroundColor: isOpened ? theme["Brand colours"].Pink : theme.surface.main.primary.hover,
    },
  };
});

// Helpers

const FAQ_CONTENT = [
  {
    question: "What is Bebop?",
    answer:
      "Bebop is a suite of decentralized trading products. Our bebop.xyz app offers easy and seamless trading to both beginner and savvy traders. Our APIs and SDK boast powerful and performant tools for any protocol, dApp or DAO trade execution, as well as for liquidity providers and solvers.",
  },
  {
    question: "How does Bebop source liquidity and ensure competitive prices?",
    answer:
      "Bebop achieves competitive prices by running an auction among its network of liquidity providers, for every trade. On top of that, we optimize for the cost of execution, for example, network (gas) fees. We source liquidity from private market makers and the so-called “solvers”, independent providers of on-chain aggregation algorithms.",
  },
  {
    question: "What makes the Bebop trading app different?",
    answer:
      "Bebop trading app brings you seamless token trading at superior guaranteed prices. Place trades for one or multiple tokens at once. Never worry about running out gas. Review your past activity. Earn achievements along your journey. All with 0 fees from Bebop.",
  },
  {
    question: "What makes Bebop APIs better?",
    answer:
      "Bebop APIs provide rich functionality at superior performance. For execution we provide a single Trade API that allows users to retrieve best prices, execute transactions or submit a trade intent. Our quote response times and fill rates are top in the industry. We provide the richest set of transaction information data via History API and enhanced token metadata via tokenlist. Bebop also offers superior interoperability with hooks for complex workflows.",
  },
  {
    question: "Does Bebop require KYC or KYB? ",
    answer:
      "We do not ask for any personal data from our users, as long as you connect a self-custodial wallet to the app, you can go ahead and trade.\n\nHowever, if as a legal entity you require to KYB your liquidity sources, we can route your order flow accordingly.",
  },
  {
    question: "Does Bebop have a token? ",
    answer: "No.",
  },
  {
    question: "Who is the team behind Bebop?",
    answer: `Bebop, as an idea, was born and incubated at Wintermute, a leading crypto liquidity powerhouse in DeFi, CeFi and Traditional Finance.\n\nBebop has been built by a team that brings different perspectives: from veteran DeFi traders, developers and “degens” to TradFi “graduates” and web2. We leverage our experience in the DeFi universe to offer game-changing improvements to execution capabilities in DeFi and best in class developer tools. For the bebop.xyz app we use the best practices from outside of DeFi, which we believe is instrumental to building a truly accessible product for wider user adoption.`,
  },
];
